import React from "react";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save, Download, Plus, Trash2, Loader2, FileText, Users, ImageIcon, FileDown } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ContractEditor(props) {
  const user = props.user;
  const setUser = props.setUser;
  const navigate = useNavigate();
  const params = useParams();
  const templateId = params.templateId;
  const contractId = params.contractId;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [template, setTemplate] = useState(null);
  const [contract, setContract] = useState(null);
  const [contractName, setContractName] = useState("");
  const [formData, setFormData] = useState({});
  const [repeatableData, setRepeatableData] = useState({});
  const [organization, setOrganization] = useState(null);

  useEffect(function() {
    fetchOrganization();
    if (contractId) {
      fetchContractData();
    } else if (templateId) {
      fetchTemplateData();
    } else {
      setLoading(false);
    }
  }, []);

  function fetchOrganization() {
    fetch(API_URL + "/api/organizations/me", { credentials: 'include' })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) { if (data) setOrganization(data); })
      .catch(function() {});
  }

  function fetchTemplateData() {
    fetch(API_URL + "/api/templates/" + templateId, { credentials: 'include' })
      .then(function(res) {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(function(data) {
        setTemplate(data);
        var init = {};
        (data.fields || []).forEach(function(f) { init[f.name] = f.default_value || ""; });
        setFormData(init);
        var initR = {};
        (data.repeatable_blocks || []).forEach(function(b) {
          var bd = {};
          (b.fields || []).forEach(function(f) { bd[f.name] = f.default_value || ""; });
          initR[b.name] = [bd];
        });
        setRepeatableData(initR);
        setLoading(false);
      })
      .catch(function() {
        toast.error("Modèle non trouvé");
        navigate('/templates');
      });
  }

  function fetchContractData() {
    fetch(API_URL + "/api/contracts/" + contractId, { credentials: 'include' })
      .then(function(res) {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(function(cdata) {
        setContract(cdata);
        setContractName(cdata.name);
        return fetch(API_URL + "/api/templates/" + cdata.template_id, { credentials: 'include' })
          .then(function(res) { return res.ok ? res.json() : null; })
          .then(function(tdata) {
            if (tdata) {
              setTemplate(tdata);
              var reg = {};
              var rep = {};
              Object.keys(cdata.data || {}).forEach(function(k) {
                if (Array.isArray(cdata.data[k])) rep[k] = cdata.data[k];
                else reg[k] = cdata.data[k];
              });
              setFormData(reg);
              setRepeatableData(rep);
            }
            setLoading(false);
          });
      })
      .catch(function() {
        toast.error("Contrat non trouvé");
        navigate('/contracts');
      });
  }

  function handleFieldChange(name, val) {
    var d = {};
    Object.keys(formData).forEach(function(k) { d[k] = formData[k]; });
    d[name] = val;
    setFormData(d);
  }

  function handleRepFieldChange(block, idx, name, val) {
    var d = {};
    Object.keys(repeatableData).forEach(function(k) { d[k] = repeatableData[k].slice(); });
    if (!d[block]) d[block] = [];
    if (!d[block][idx]) d[block][idx] = {};
    var entry = {};
    Object.keys(d[block][idx]).forEach(function(k) { entry[k] = d[block][idx][k]; });
    entry[name] = val;
    d[block][idx] = entry;
    setRepeatableData(d);
  }

  function addEntry(blockName) {
    var blk = (template && template.repeatable_blocks || []).find(function(b) { return b.name === blockName; });
    if (!blk) return;
    var ne = {};
    (blk.fields || []).forEach(function(f) { ne[f.name] = f.default_value || ""; });
    var d = {};
    Object.keys(repeatableData).forEach(function(k) { d[k] = repeatableData[k].slice(); });
    d[blockName] = (d[blockName] || []).concat([ne]);
    setRepeatableData(d);
  }

  function removeEntry(blockName, idx) {
    var arr = repeatableData[blockName] || [];
    if (arr.length <= 1) return;
    var d = {};
    Object.keys(repeatableData).forEach(function(k) { d[k] = repeatableData[k].slice(); });
    d[blockName] = arr.filter(function(_, i) { return i !== idx; });
    setRepeatableData(d);
  }

  function doSave() {
    if (!contractName.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    var all = {};
    Object.keys(formData).forEach(function(k) { all[k] = formData[k]; });
    Object.keys(repeatableData).forEach(function(k) { all[k] = repeatableData[k]; });
    var payload = { template_id: template.template_id, name: contractName, data: all, logo_url: organization && organization.logo_url };
    var url = contract ? API_URL + "/api/contracts/" + contract.contract_id : API_URL + "/api/contracts";
    var method = contract ? 'PUT' : 'POST';
    fetch(url, { method: method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) })
      .then(function(res) { return res.ok ? res.json() : Promise.reject(); })
      .then(function(data) {
        setContract(data);
        toast.success(contract ? "Mis à jour" : "Créé");
        if (!contract) navigate("/contracts/" + data.contract_id, { replace: true });
      })
      .catch(function() { toast.error("Erreur"); })
      .finally(function() { setSaving(false); });
  }

  function doGenerate(fmt) {
    if (!contract) { toast.error("Sauvegardez d'abord"); return; }
    if (fmt === "pdf") setGeneratingPdf(true); else setGenerating(true);
    fetch(API_URL + "/api/contracts/" + contract.contract_id + "/generate?format=" + fmt, { method: 'POST', credentials: 'include' })
      .then(function(res) { return res.ok ? res.json() : Promise.reject(); })
      .then(function(data) { window.open(API_URL + data.file_url, '_blank'); toast.success("Généré"); })
      .catch(function() { toast.error("Erreur"); })
      .finally(function() { setGenerating(false); setGeneratingPdf(false); });
  }

  if (loading) {
    return React.createElement(AppLayout, { user: user, setUser: setUser },
      React.createElement("div", { className: "flex items-center justify-center h-screen" },
        React.createElement("div", { className: "spinner w-8 h-8" })
      )
    );
  }

  var logoUrl = organization && organization.logo_url ? API_URL + organization.logo_url : null;
  var fields = template && template.fields || [];
  var blocks = template && template.repeatable_blocks || [];

  return React.createElement(AppLayout, { user: user, setUser: setUser },
    React.createElement("div", { className: "p-8", "data-testid": "contract-editor-page" },
      React.createElement("div", { className: "flex items-center justify-between mb-8" },
        React.createElement("div", { className: "flex items-center gap-4" },
          React.createElement(Button, { variant: "ghost", size: "icon", onClick: function() { navigate(-1); } },
            React.createElement(ArrowLeft, { className: "w-4 h-4" })
          ),
          React.createElement("div", null,
            React.createElement("h1", { className: "font-heading text-2xl font-semibold tracking-tight" },
              contract ? "Modifier le contrat" : "Nouveau contrat"
            ),
            React.createElement("p", { className: "text-sm text-muted-foreground" },
              "Modèle: " + (template && template.name || "")
            )
          )
        ),
        React.createElement("div", { className: "flex items-center gap-2" },
          contract && React.createElement(Button, { variant: "outline", onClick: function() { doGenerate("docx"); }, disabled: generating, "data-testid": "generate-btn" },
            generating ? React.createElement(Loader2, { className: "w-4 h-4 mr-2 animate-spin" }) : React.createElement(Download, { className: "w-4 h-4 mr-2" }),
            "Word"
          ),
          contract && React.createElement(Button, { variant: "outline", onClick: function() { doGenerate("pdf"); }, disabled: generatingPdf, "data-testid": "generate-pdf-btn" },
            generatingPdf ? React.createElement(Loader2, { className: "w-4 h-4 mr-2 animate-spin" }) : React.createElement(FileDown, { className: "w-4 h-4 mr-2" }),
            "PDF"
          ),
          React.createElement(Button, { onClick: doSave, disabled: saving, "data-testid": "save-contract-btn" },
            saving ? React.createElement(Loader2, { className: "w-4 h-4 mr-2 animate-spin" }) : React.createElement(Save, { className: "w-4 h-4 mr-2" }),
            "Sauvegarder"
          )
        )
      ),
      React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-8" },
        React.createElement("div", { className: "lg:col-span-2 space-y-6" },
          React.createElement(Card, null,
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
                React.createElement(FileText, { className: "w-5 h-5" }),
                "Informations du contrat"
              )
            ),
            React.createElement(CardContent, null,
              React.createElement("div", { className: "space-y-2" },
                React.createElement(Label, { htmlFor: "contractName" }, "Nom du contrat *"),
                React.createElement(Input, {
                  id: "contractName",
                  placeholder: "Ex: Contrat de travail - Jean Dupont",
                  value: contractName,
                  onChange: function(e) { setContractName(e.target.value); },
                  "data-testid": "contract-name-input"
                })
              )
            )
          ),
          fields.length > 0 && React.createElement(Card, null,
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: "text-lg" }, "Champs du contrat"),
              React.createElement(CardDescription, null, "Remplissez les informations requises")
            ),
            React.createElement(CardContent, null,
              React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
                fields.map(function(f, i) {
                  return React.createElement("div", { key: "f" + i, className: "space-y-2" },
                    React.createElement(Label, { htmlFor: f.name },
                      f.label,
                      f.required && React.createElement("span", { className: "text-destructive ml-1" }, "*")
                    ),
                    f.field_type === 'textarea'
                      ? React.createElement(Textarea, { id: f.name, value: formData[f.name] || "", onChange: function(e) { handleFieldChange(f.name, e.target.value); }, placeholder: f.label })
                      : React.createElement(Input, { id: f.name, type: f.field_type === 'date' ? 'date' : 'text', value: formData[f.name] || "", onChange: function(e) { handleFieldChange(f.name, e.target.value); }, placeholder: f.label })
                  );
                })
              )
            )
          ),
          blocks.map(function(b, bi) {
            var entries = repeatableData[b.name] || [];
            return React.createElement(Card, { key: "b" + bi },
              React.createElement(CardHeader, null,
                React.createElement("div", { className: "flex items-center justify-between" },
                  React.createElement("div", null,
                    React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
                      React.createElement(Users, { className: "w-5 h-5" }),
                      b.name
                    ),
                    React.createElement(CardDescription, null, "Bloc répétable multi-parties")
                  ),
                  React.createElement(Button, { variant: "outline", size: "sm", onClick: function() { addEntry(b.name); } },
                    React.createElement(Plus, { className: "w-4 h-4 mr-1" }),
                    "Ajouter"
                  )
                )
              ),
              React.createElement(CardContent, { className: "space-y-6" },
                entries.map(function(entry, ei) {
                  return React.createElement("div", { key: "e" + ei, className: "party-section" },
                    React.createElement("div", { className: "party-header" },
                      React.createElement(Badge, { variant: "secondary" }, b.name + " n°" + (ei + 1)),
                      entries.length > 1 && React.createElement(Button, { variant: "ghost", size: "icon", onClick: function() { removeEntry(b.name, ei); }, className: "text-destructive" },
                        React.createElement(Trash2, { className: "w-4 h-4" })
                      )
                    ),
                    React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
                      (b.fields || []).map(function(f, fi) {
                        return React.createElement("div", { key: "bf" + fi, className: "space-y-2" },
                          React.createElement(Label, null, f.label),
                          React.createElement(Input, { value: entry[f.name] || "", onChange: function(e) { handleRepFieldChange(b.name, ei, f.name, e.target.value); }, placeholder: f.label })
                        );
                      })
                    )
                  );
                })
              )
            );
          })
        ),
        React.createElement("div", { className: "space-y-6" },
          React.createElement(Card, null,
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
                React.createElement(ImageIcon, { className: "w-5 h-5" }),
                "Logo du contrat"
              ),
              React.createElement(CardDescription, null, "Logo affiché sur la première page")
            ),
            React.createElement(CardContent, null,
              logoUrl
                ? React.createElement("div", { className: "space-y-4" },
                    React.createElement("div", { className: "p-4 border rounded-lg flex items-center justify-center bg-white" },
                      React.createElement("img", { src: logoUrl, alt: "Logo", className: "max-h-20 object-contain" })
                    ),
                    React.createElement("p", { className: "text-xs text-muted-foreground text-center" }, "Logo de " + (organization && organization.name || ""))
                  )
                : React.createElement("div", { className: "text-center py-4" },
                    React.createElement(ImageIcon, { className: "w-12 h-12 text-muted-foreground mx-auto mb-2" }),
                    React.createElement("p", { className: "text-sm text-muted-foreground mb-2" }, "Aucun logo configuré"),
                    React.createElement(Button, { variant: "outline", size: "sm", onClick: function() { navigate('/organization'); } }, "Configurer")
                  )
            )
          ),
          contract && React.createElement(Card, null,
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: "text-lg" }, "Informations")
            ),
            React.createElement(CardContent, { className: "space-y-3" },
              React.createElement("div", { className: "flex justify-between text-sm" },
                React.createElement("span", { className: "text-muted-foreground" }, "Version"),
                React.createElement(Badge, { variant: "outline" }, "v" + contract.version)
              ),
              React.createElement("div", { className: "flex justify-between text-sm" },
                React.createElement("span", { className: "text-muted-foreground" }, "Statut"),
                React.createElement(Badge, { className: "bg-yellow-100 text-yellow-800" }, contract.status)
              ),
              React.createElement(Separator, null),
              React.createElement(Button, { variant: "outline", className: "w-full", onClick: function() { navigate("/contracts/" + contract.contract_id + "/versions"); } },
                "Voir l'historique"
              )
            )
          )
        )
      )
    )
  );
}

export default ContractEditor;
